import { useState, useCallback } from 'react'
import type { AuthState, User, AuthFormErrors } from '../types/auth'
import { validateEmail, validatePassword, validateConfirmPassword } from '../utils/validation'
import { amplitudeService } from '../services/amplitude.service'

const useAuth = () => {
  const [state, setState] = useState<AuthState>({
    user: null,
    isLoading: false,
    error: null,
    isAuthenticated: false,
  })

  const clearError = useCallback(() => {
    setState((prev) => ({ ...prev, error: null }))
  }, [])

  const setLoading = useCallback((isLoading: boolean) => {
    setState((prev) => ({ ...prev, isLoading }))
  }, [])

  const setError = useCallback((error: string) => {
    setState((prev) => ({ ...prev, error, isLoading: false }))
  }, [])

  const setUser = useCallback((user: User | null) => {
    setState((prev) => ({
      ...prev,
      user,
      isAuthenticated: !!user,
      isLoading: false,
      error: null,
    }))

    // Identify user in Amplitude when they log in
    if (user) {
      amplitudeService.identify(user.id, {
        email: user.email,
        name: user.name,
        createdAt: user.createdAt.toISOString(),
      })
    }
  }, [])

  // Mock login function - replace with real API call
  const login = useCallback(
    async (email: string, _password: string) => {
      setLoading(true)
      clearError()

      try {
        // Simulate API call
        await new Promise((resolve) => setTimeout(resolve, 1000))

        // Mock user data
        const mockUser: User = {
          id: '1',
          email,
          name: email.split('@')[0],
          createdAt: new Date(),
        }

        setUser(mockUser)
      } catch {
        setError('Invalid email or password. Please try again.')
      }
    },
    [setLoading, clearError, setUser, setError]
  )

  // Mock signup function - replace with real API call
  const signup = useCallback(
    async (email: string, _password: string) => {
      setLoading(true)
      clearError()

      try {
        // Simulate API call
        await new Promise((resolve) => setTimeout(resolve, 1000))

        // Mock user data
        const mockUser: User = {
          id: '1',
          email,
          name: email.split('@')[0],
          createdAt: new Date(),
        }

        setUser(mockUser)
      } catch {
        setError('Failed to create account. Please try again.')
      }
    },
    [setLoading, clearError, setUser, setError]
  )

  // Mock Google OAuth function - replace with real implementation
  const loginWithGoogle = useCallback(async () => {
    setLoading(true)
    clearError()

    try {
      // Simulate Google OAuth flow
      await new Promise((resolve) => setTimeout(resolve, 1500))

      // Mock user data from Google
      const mockUser: User = {
        id: '1',
        email: 'user@gmail.com',
        name: 'Google User',
        avatar: 'https://via.placeholder.com/40',
        createdAt: new Date(),
      }

      setUser(mockUser)
    } catch {
      setError('Google authentication failed. Please try again.')
    }
  }, [setLoading, clearError, setUser, setError])

  const logout = useCallback(async () => {
    setLoading(true)

    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 500))
      setUser(null)
      amplitudeService.reset() // Reset Amplitude user
    } catch {
      setError('Failed to logout. Please try again.')
    }
  }, [setLoading, setUser, setError])

  // Form validation helper
  const validateForm = useCallback(
    (
      email: string,
      password: string,
      confirmPassword?: string,
      mode: 'login' | 'signup' = 'login'
    ): AuthFormErrors => {
      const errors: AuthFormErrors = {}

      const emailError = validateEmail(email)
      if (emailError) errors.email = emailError

      const passwordError = validatePassword(password)
      if (passwordError) errors.password = passwordError

      if (mode === 'signup' && confirmPassword !== undefined) {
        const confirmError = validateConfirmPassword(password, confirmPassword)
        if (confirmError) errors.confirmPassword = confirmError
      }

      return errors
    },
    []
  )

  return {
    ...state,
    login,
    signup,
    loginWithGoogle,
    logout,
    clearError,
    validateForm,
  }
}

export default useAuth
